/**
 * Shared sort constants and helpers used across collection, set, and deck sorting.
 *
 * Canonical source for WUBRG order, rarity order, and collector number parsing.
 * Import from here instead of defining local copies.
 */

// ============================================================================
// Rarity
// ============================================================================

/** Rarity sort order (common = 0 → bonus = 5) */
export const RARITY_ORDER: Record<string, number> = {
  common: 0,
  uncommon: 1,
  rare: 2,
  mythic: 3,
  special: 4,
  bonus: 5,
};

// ============================================================================
// Color
// ============================================================================

/** WUBRG color sort order for single colors */
export const COLOR_ORDER: Record<string, number> = {
  W: 0,
  U: 1,
  B: 2,
  R: 3,
  G: 4,
};

/**
 * Get color sort value — prioritizes by color count then WUBRG order.
 * Colorless = 100 (last), multicolor = 50+count, single = WUBRG index.
 */
export function getColorSortValue(colors: string[]): number {
  if (colors.length === 0) return 100; // Colorless last
  if (colors.length > 1) return 50 + colors.length; // Multicolor in middle
  return COLOR_ORDER[colors[0]] ?? 10; // Single color by WUBRG
}

// ============================================================================
// Condition & Finish
// ============================================================================

/** Condition sort order (best to worst) */
export const CONDITION_ORDER: Record<string, number> = {
  M: 0,
  NM: 1,
  LP: 2,
  MP: 3,
  HP: 4,
  D: 5,
};

/** Finish sort order */
export const FINISH_ORDER: Record<string, number> = {
  nonfoil: 0,
  foil: 1,
  etched: 2,
};

// ============================================================================
// Collector Number
// ============================================================================

/**
 * Parse collector number for sorting — handles numeric and alphanumeric values.
 * Returns structured result for stable multi-key sort (number first, suffix second).
 *
 * Examples: "1" → {0, ""}, "123a" → {123, "a"}, "★1" → {Infinity, "★1"}
 */
export function parseCollectorNumber(cn: string): { num: number; suffix: string } {
  const match = cn.match(/^(\d+)(.*)$/);
  if (match) {
    return { num: parseInt(match[1], 10), suffix: match[2] };
  }
  return { num: Infinity, suffix: cn };
}

/**
 * Parse collector number returning only the numeric portion.
 * Simpler variant for contexts that don't need suffix-level ordering.
 */
export function parseCollectorNumberSimple(cn: string): number {
  const match = cn.match(/\d+/);
  if (match) {
    return parseInt(match[0], 10);
  }
  return Infinity;
}
